import { useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AuthShell } from '@/components/auth/auth-shell';
import { Button } from '@/components/ui/button';
import { authClient } from '@/lib/auth-client';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  // The token may be in the URL if the user clicked the link from email.
  const search = useSearch({ strict: false }) as { token?: string };
  const [verifying, setVerifying] = useState(!!search.token);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!search.token) return;
    void (async () => {
      setVerifying(true);
      try {
        const { error } = await authClient.verifyEmail({
          query: { token: search.token as string },
        });
        if (error) {
          toast.error(error.message ?? 'Verification failed');
        } else {
          setVerified(true);
          setTimeout(() => void navigate({ to: '/onboarding' }), 1500);
        }
      } catch {
        toast.error('Something went wrong');
      } finally {
        setVerifying(false);
      }
    })();
  }, [search.token, navigate]);

  if (verifying) {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <p className="text-center text-sm text-faint">Verifying your email…</p>
        </AuthShell>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
            <h1 className="mt-4 text-lg font-semibold">Email verified!</h1>
            <p className="mt-1 text-sm text-faint">Redirecting you now…</p>
          </div>
        </AuthShell>
      </div>
    );
  }

  // No token — user landed here from the signup form.
  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        <div className="text-center">
          <Mail className="mx-auto h-10 w-10 text-foreground" />
          <h1 className="mt-3 text-lg font-semibold">Check your email</h1>
          <p className="mt-1.5 text-sm text-faint">
            We&apos;ve sent you a verification link. Click it to activate your account — then come
            back here to continue.
          </p>
        </div>
        <Button
          variant="outline"
          size="cta"
          className="mt-6 w-full"
          onClick={() => void navigate({ to: '/onboarding' })}
        >
          I&apos;ll verify later
        </Button>
      </AuthShell>
    </div>
  );
}
